import { getAuth } from "firebase/auth";
import { redirect } from "react-router-dom";
import {
  LISTING_LIST_QUERY_KEYS,
  type ListingBrowseLoaderResult,
  type ListingBrowsePageLoaderResult,
  type ListingPropertyDetailsInfo,
} from "~/types/listing";
import { apiClient } from "~/services/apiClient";
import { encodeLocationsForApi, parseLocationsFromSearchParams } from "~/utils/listingLocations";
import { redirectToSignupWithReturnUrl } from "~/utils";

function buildListingListParams(request: Request): Record<string, string> {
  const url = new URL(request.url);
  const params: Record<string, string> = {};

  for (const key of LISTING_LIST_QUERY_KEYS) {
    if (key === "location") continue;
    const value = url.searchParams.get(key)?.trim();
    if (value) {
      params[key] = value;
    }
  }

  const encodedLocations = encodeLocationsForApi(
    parseLocationsFromSearchParams(url.searchParams)
  );
  if (encodedLocations) {
    params.location = encodedLocations;
  }

  const legacyPropertyId = url.searchParams.get("propertyID")?.trim();
  if (legacyPropertyId && !params.propertyId) {
    params.propertyId = legacyPropertyId;
  }

  return params;
}

export async function loadListingBrowse(
  request: Request,
  options?: { lockedPropertyId?: string; loadPropertyInfo?: boolean }
): Promise<ListingBrowsePageLoaderResult> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    return redirectToSignupWithReturnUrl(request) as never;
  }

  const lockedPropertyId = options?.lockedPropertyId?.trim();
  const params = buildListingListParams(request);
  if (lockedPropertyId) {
    params.propertyId = lockedPropertyId;
  }

  const loadPropertyInfo = options?.loadPropertyInfo && lockedPropertyId;

  const [listingResponse, propertyInfoResponse] = await Promise.all([
    apiClient.loader<ListingBrowseLoaderResult>("listing-list", params),
    loadPropertyInfo
      ? apiClient.loader<ListingPropertyDetailsInfo>("listing-property-info", {
          id: lockedPropertyId,
        })
      : Promise.resolve({ success: true as const, data: undefined }),
  ]);

  if (!listingResponse.success || !listingResponse.data) {
    return {
      items: [],
      total: 0,
      page: 1,
      pageSize: 10,
      propertyOptions: [],
      lockedPropertyId,
      propertyName: propertyInfoResponse.data?.name,
      propertyInfo: propertyInfoResponse.data ?? undefined,
    };
  }

  const propertyInfo = propertyInfoResponse.data ?? undefined;
  const propertyName = lockedPropertyId
    ? (propertyInfo?.name ??
      listingResponse.data.propertyOptions.find((o) => o.id === lockedPropertyId)
        ?.name ??
      listingResponse.data.items.find((item) => item.propertyID === lockedPropertyId)
        ?.propertyName)
    : undefined;

  return {
    ...listingResponse.data,
    lockedPropertyId,
    propertyName,
    propertyInfo,
  };
}
